import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CalendarClock, Plus, Play, Power, Trash2, Repeat, Loader2, Pencil, HardHat, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { TYPE_LABEL, PRIORITY_LABEL, formatDateTime } from "@/lib/format";

const TYPES = ["maintenance", "product_issue", "structural_damage", "miscellaneous"];
const PRIORITIES = ["urgent", "general", "routine"];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const emptyForm = () => ({
  outlet_id: "", type: "maintenance", priority: "routine",
  title: "", description: "",
  frequency: "weekly", schedule_day: 0, interval_days: 7, is_active: true,
  vendor_id: "", asset_id: "",
});

export default function Recurring() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [assets, setAssets] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const canInternal = user?.role === "admin";
  const typeOptions = canInternal ? [...TYPES, "internal_job"] : TYPES;
  const outletChoices = outlets.filter(o => form.type === "internal_job" ? o.is_internal : !o.is_internal);
  const assetChoices = assets.filter(a => a.outlet_id === form.outlet_id);

  const load = async () => {
    const [r, o, v, a] = await Promise.all([
      api.get("/recurring"),
      api.get("/outlets"),
      api.get("/vendors").catch(() => ({ data: [] })),
      api.get("/assets").catch(() => ({ data: [] })),
    ]);
    setItems(r.data);
    setOutlets(o.data);
    setVendors(v.data);
    setAssets(a.data);
  };
  useEffect(() => { load(); }, []);

  // If the picked type/outlet combo is no longer valid, clear outlet & any linked asset
  useEffect(() => {
    if (form.outlet_id && !outletChoices.some(o => o.id === form.outlet_id)) {
      setForm(f => ({ ...f, outlet_id: outletChoices[0]?.id || "", asset_id: "" }));
    }
    // eslint-disable-next-line
  }, [form.type, outlets]);

  // If the outlet changes, drop any asset that doesn't belong to the new outlet
  useEffect(() => {
    if (form.asset_id && !assets.some(a => a.id === form.asset_id && a.outlet_id === form.outlet_id)) {
      setForm(f => ({ ...f, asset_id: "" }));
    }
    // eslint-disable-next-line
  }, [form.outlet_id]);

  const outletMap = Object.fromEntries(outlets.map(o => [o.id, o]));

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm(), outlet_id: outlets[0]?.id || "" });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      outlet_id: r.outlet_id, type: r.type, priority: r.priority,
      title: r.title, description: r.description,
      frequency: r.frequency, schedule_day: r.schedule_day ?? 0,
      interval_days: r.interval_days ?? 7, is_active: r.is_active,
      vendor_id: r.vendor_id || "", asset_id: r.asset_id || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.outlet_id) return toast.error("Pick an outlet");
    if (form.title.trim().length < 3) return toast.error("Title too short");
    setSaving(true);
    try {
      const payload = {
        ...form,
        title: form.title.trim(), description: form.description.trim(),
        schedule_day: form.frequency === "interval" ? null : parseInt(form.schedule_day, 10),
        interval_days: form.frequency === "interval" ? parseInt(form.interval_days, 10) : null,
        vendor_id: form.vendor_id || null,
        asset_id: form.asset_id || null,
      };
      if (editing) await api.put(`/recurring/${editing.id}`, payload);
      else await api.post("/recurring", payload);
      toast.success(editing ? "Schedule updated" : "Recurring schedule created");
      setOpen(false); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const runNow = async (r) => {
    try {
      await api.post(`/recurring/${r.id}/run-now`);
      toast.success("Ticket raised from schedule");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const toggleActive = async (r) => {
    try {
      await api.put(`/recurring/${r.id}`, { is_active: !r.is_active });
      load();
    } catch { toast.error("Failed"); }
  };
  const remove = async (r) => {
    if (!confirm(`Delete recurring schedule "${r.title}"?`)) return;
    try { await api.delete(`/recurring/${r.id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Failed"); }
  };

  const scheduleText = (r) => {
    if (r.frequency === "weekly") return `Every ${WEEKDAYS[r.schedule_day ?? 0]}, 09:00 UTC`;
    if (r.frequency === "monthly") return `Day ${r.schedule_day} of every month, 09:00 UTC`;
    return `Every ${r.interval_days} day${r.interval_days === 1 ? "" : "s"}`;
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-3xl text-slate-900">Recurring schedules</h1>
          <p className="text-sm text-slate-500 mt-1">
            Auto-raise routine tickets (AC service, deep-clean, monthly audits) on a schedule.
          </p>
        </div>
        <Button data-testid="new-recurring-btn" onClick={openCreate}
          className="h-11 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> New schedule
        </Button>
      </div>

      {items === null ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[0,1].map(i => <div key={i} className="h-36 rounded-2xl bg-white border border-slate-200 animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <CalendarClock className="w-8 h-8 text-slate-400 mx-auto" />
          <div className="mt-3 font-semibold text-slate-800">No recurring schedules yet</div>
          <div className="text-sm text-slate-500">Create one to auto-raise routine tickets on a cadence.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((r) => (
            <div key={r.id} data-testid={`recurring-card-${r.id}`}
              className={`rounded-2xl bg-white border p-5 card-lift ${r.is_active ? "border-slate-200" : "border-slate-200 opacity-70"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5 mb-1">
                    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-0.5 text-[11px] font-semibold">
                      <Repeat className="w-3 h-3" /> {scheduleText(r)}
                    </span>
                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600">
                      {TYPE_LABEL[r.type]}
                    </span>
                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 uppercase tracking-wide">
                      {PRIORITY_LABEL[r.priority]}
                    </span>
                  </div>
                  <div className="font-display font-bold text-lg text-slate-900">{r.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {outletMap[r.outlet_id]?.code} · {outletMap[r.outlet_id]?.name || "—"}
                  </div>
                  {(r.vendor_name || r.asset_name) && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      {r.vendor_name && (
                        <span data-testid={`recurring-vendor-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-900 px-2 py-0.5 text-[11px] font-semibold">
                          <HardHat className="w-3 h-3" /> {r.vendor_name}
                        </span>
                      )}
                      {r.asset_name && (
                        <span data-testid={`recurring-asset-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-sky-50 border border-sky-200 text-sky-900 px-2 py-0.5 text-[11px] font-semibold">
                          <Package className="w-3 h-3" /> {r.asset_name}
                        </span>
                      )}
                    </div>
                  )}
                  {r.description && <div className="text-xs text-slate-600 mt-2 line-clamp-2">{r.description}</div>}
                  <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                    <div>
                      <div className="text-slate-500 uppercase tracking-wide text-[10px] font-semibold">Scheduled For</div>
                      <div data-testid={`recurring-next-${r.id}`} className="font-semibold text-slate-900 tabular-nums">{formatDateTime(r.next_run_at)}</div>
                    </div>
                    <div>
                      <div className="text-slate-500 uppercase tracking-wide text-[10px] font-semibold">Last Auto-Run</div>
                      <div data-testid={`recurring-last-${r.id}`} className="font-semibold text-slate-900 tabular-nums">{r.last_run_at ? formatDateTime(r.last_run_at) : "—"}</div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Switch checked={r.is_active} onCheckedChange={() => toggleActive(r)} data-testid={`recurring-toggle-${r.id}`} />
                  <span className="text-[10px] uppercase tracking-wide text-slate-500">{r.is_active ? "Active" : "Paused"}</span>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => runNow(r)} data-testid={`recurring-run-${r.id}`}>
                  <Play className="w-3.5 h-3.5 mr-1" /> Run now
                </Button>
                <Button variant="outline" size="sm" onClick={() => openEdit(r)} data-testid={`recurring-edit-${r.id}`}>
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
                <Button variant="ghost" size="sm" onClick={() => remove(r)} data-testid={`recurring-delete-${r.id}`}
                  className="text-rose-600 hover:bg-rose-50 hover:text-rose-700">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit schedule" : "New recurring schedule"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Outlet</Label>
              <Select value={form.outlet_id} onValueChange={(v) => setForm({ ...form, outlet_id: v })}>
                <SelectTrigger data-testid="rec-outlet" className="mt-1.5 h-11"><SelectValue placeholder={outletChoices.length ? "Choose outlet" : "No outlet available for this type"} /></SelectTrigger>
                <SelectContent>
                  {outletChoices.map((o) => <SelectItem key={o.id} value={o.id}>{o.code} · {o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="rec-type" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {typeOptions.map((t) => <SelectItem key={t} value={t}>{TYPE_LABEL[t]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="rec-priority" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABEL[p]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Title</Label>
              <Input data-testid="rec-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1.5 h-11" placeholder="e.g. Weekly AC deep-service" maxLength={140} required />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea data-testid="rec-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="mt-1.5 min-h-20" placeholder="Any details the FC should see on the ticket" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="flex items-center gap-1.5"><HardHat className="w-3.5 h-3.5 text-slate-500" /> Vendor <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Select value={form.vendor_id || "__none__"} onValueChange={(v) => setForm({ ...form, vendor_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="rec-vendor" className="mt-1.5 h-11"><SelectValue placeholder="No vendor" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No vendor</SelectItem>
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name}{v.trade ? ` · ${v.trade}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="flex items-center gap-1.5"><Package className="w-3.5 h-3.5 text-slate-500" /> Asset <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Select value={form.asset_id || "__none__"} onValueChange={(v) => setForm({ ...form, asset_id: v === "__none__" ? "" : v })} disabled={!form.outlet_id || form.type === "internal_job"}>
                  <SelectTrigger data-testid="rec-asset" className="mt-1.5 h-11">
                    <SelectValue placeholder={!form.outlet_id ? "Pick outlet first" : assetChoices.length ? "No specific asset" : "No assets for this outlet"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No specific asset</SelectItem>
                    {assetChoices.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.name}{a.category ? ` · ${a.category}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Frequency</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger data-testid="rec-freq" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="interval">Every N days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.frequency === "weekly" && (
              <div>
                <Label>Day of week</Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {WEEKDAYS.map((d, i) => (
                    <button key={d} type="button" data-testid={`rec-dow-${i}`}
                      onClick={() => setForm({ ...form, schedule_day: i })}
                      className={`h-9 px-3 rounded-full text-xs font-semibold border transition-colors ${
                        Number(form.schedule_day) === i ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                      }`}>{d}</button>
                  ))}
                </div>
              </div>
            )}

            {form.frequency === "monthly" && (
              <div>
                <Label>Day of month (1–28)</Label>
                <Input data-testid="rec-dom" type="number" min="1" max="28" value={form.schedule_day}
                  onChange={(e) => setForm({ ...form, schedule_day: e.target.value })}
                  className="mt-1.5 h-11 max-w-32" />
              </div>
            )}

            {form.frequency === "interval" && (
              <div>
                <Label>Every N days</Label>
                <Input data-testid="rec-interval" type="number" min="1" value={form.interval_days}
                  onChange={(e) => setForm({ ...form, interval_days: e.target.value })}
                  className="mt-1.5 h-11 max-w-32" />
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="rec-active" />
              <span className="text-sm text-slate-700">Active — auto-raise on schedule</span>
            </div>

            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="rec-save"
                className="bg-indigo-600 hover:bg-indigo-700 text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? "Save changes" : "Create schedule")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
